import React, { useEffect, useState } from "react";
import { api } from "../utils/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { ArrowLeft, Calculator, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PaymentBreakupForm from "./PaymentBreakupForm";

const InvoiceCreationForm = ({ quotation, onSuccess, onCancel }) => {
    const pricing = quotation.detailed_quotation_data?.pricing || {};
    const inputCurrency = quotation.detailed_quotation_data?.inputCurrency || "INR";
    const originalPricing = quotation.detailed_quotation_data?.original_currency_data?.pricing || {};

    const formatOriginalCurrency = (amount, currencyCode) => {
        if (!currencyCode || currencyCode === "INR") return "";
        try {
            return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: currencyCode,
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(amount);
        } catch (e) {
            return `${currencyCode} ${Number(amount).toFixed(2)}`;
        }
    };

    const [step, setStep] = useState(1); // 1: Invoice Details, 2: Payment Breakup
    const [loading, setLoading] = useState(false);
    const [createdInvoice, setCreatedInvoice] = useState(null);

    // Invoice form state
    const [tcsEnabled, setTcsEnabled] = useState(true);
    const [tcsPercent, setTcsPercent] = useState(2.0);
    const [subtotal, setSubtotal] = useState(pricing.subtotal || 0);
    const [taxAmount, setTaxAmount] = useState(pricing.taxes || 0);
    // const [advanceAmount, setAdvanceAmount] = useState(pricing.depositDue || 0);
    const [tcsAmount, setTcsAmount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [items, setItems] = useState([]);

    // useEffect(() => {
    //     if (items.length > 0) {
    //         const totalAmount = items.reduce(
    //             (acc, item) => acc + item.total_amount,
    //             0,
    //         );
    //         setTotalAmount(totalAmount);
    //         const taxAmount = items.reduce(
    //             (acc, item) =>
    //                 acc +
    //                 item.unit_price * item.quantity * (item.tax_percent / 100),
    //             0,
    //         );
    //         setTaxAmount(taxAmount);

    //         if (tcsEnabled) {
    //             const tcsAmount = items.reduce(
    //                 (acc, item) =>
    //                     acc +
    //                     item.unit_price * item.quantity * (tcsPercent / 100),
    //                 0,
    //             );
    //             setTcsAmount(tcsAmount);
    //         }
    //     }
    // }, [items, tcsEnabled, tcsPercent]);

    useEffect(() => {
        const subtotal = items.reduce(
            (acc, item) =>
                acc + Number(item.quantity) * Number(item.unit_price),
            0
        );

        const tax = items.reduce(
            (acc, item) =>
                acc +
                Number(item.quantity) *
                Number(item.unit_price) *
                (Number(item.tax_percent) / 100),
            0
        );

        const baseForTcs = subtotal + tax;

        const tcs = tcsEnabled
            ? baseForTcs * (Number(tcsPercent) / 100)
            : 0;

        setTaxAmount(tax);
        setTcsAmount(tcs);
        setTotalAmount(baseForTcs + tcs);
        setSubtotal(subtotal);
    }, [items, tcsEnabled, tcsPercent]);

    useEffect(() => {
        if (items.length === 0 && pricing) {
            const getCategoryPricing = (segmentItems = []) => {
                let totalCost = 0;
                let baseCost = 0;
                const arr = Array.isArray(segmentItems) ? segmentItems : [];
                arr.forEach(item => {
                    const gst = Number(item.gst_percentage) || 0;
                    const total = Number(item.total_cost) || 0;
                    totalCost += total;
                    baseCost += total / (1 + gst / 100);
                });
                const gstPercent = baseCost > 0 ? ((totalCost - baseCost) / baseCost) * 100 : 0;
                return {
                    totalCost,
                    baseCost: Math.round(baseCost),
                    gstPercent: Math.round(gstPercent * 100) / 100
                };
            };

            const initialItems = [];

            // Flight
            const airData = getCategoryPricing(quotation.detailed_quotation_data?.flights);
            const airBase = airData.totalCost > 0 ? airData.baseCost : (pricing.airCost || 0);
            const airGst = airData.totalCost > 0 ? airData.gstPercent : 0;
            if (pricing.airCost > 0) {
                initialItems.push({
                    description: "Air Ticket / Flight Cost",
                    hsn_sac: "9964",
                    quantity: 1,
                    unit_price: airBase,
                    tax_percent: airGst,
                    tax_amount: airBase * (airGst / 100),
                    total_amount: airBase * (1 + airGst / 100)
                });
            }

            // Visa
            const visaData = getCategoryPricing(quotation.detailed_quotation_data?.visas);
            const visaBase = visaData.totalCost > 0 ? visaData.baseCost : (pricing.visaCost || 0);
            const visaGst = visaData.totalCost > 0 ? visaData.gstPercent : 0;
            if (pricing.visaCost > 0) {
                initialItems.push({
                    description: "Visa Cost",
                    hsn_sac: "9997",
                    quantity: 1,
                    unit_price: visaBase,
                    tax_percent: visaGst,
                    tax_amount: visaBase * (visaGst / 100),
                    total_amount: visaBase * (1 + visaGst / 100)
                });
            }

            // Railway
            const trainData = getCategoryPricing(quotation.detailed_quotation_data?.trains);
            const trainBase = trainData.totalCost > 0 ? trainData.baseCost : (pricing.railwayCost || 0);
            const trainGst = trainData.totalCost > 0 ? trainData.gstPercent : 0;
            if (pricing.railwayCost > 0) {
                initialItems.push({
                    description: "Railway Tickets",
                    hsn_sac: "9964",
                    quantity: 1,
                    unit_price: trainBase,
                    tax_percent: trainGst,
                    tax_amount: trainBase * (trainGst / 100),
                    total_amount: trainBase * (1 + trainGst / 100)
                });
            }

            // Package
            const packageItems = [
                ...(quotation.detailed_quotation_data?.standalone_hotels || []),
                ...(quotation.detailed_quotation_data?.mice_events || []),
                ...(quotation.detailed_quotation_data?.sightseeing_packages || []),
                ...(quotation.detailed_quotation_data?.transports_within_city || []),
                ...(quotation.detailed_quotation_data?.buses || []),
                ...(quotation.detailed_quotation_data?.days || [])
            ];
            const packageData = getCategoryPricing(packageItems);
            const packageBase = packageData.totalCost > 0 ? packageData.baseCost : (pricing.packageCost || 0);
            const packageGst = packageData.totalCost > 0 ? packageData.gstPercent : 0;
            if (pricing.packageCost > 0) {
                initialItems.push({
                    description: "Package Cost / Service Cost",
                    hsn_sac: "9985",
                    quantity: 1,
                    unit_price: packageBase,
                    tax_percent: packageGst,
                    tax_amount: packageBase * (packageGst / 100),
                    total_amount: packageBase * (1 + packageGst / 100)
                });
            }

            // Taxes
            if (pricing.taxes > 0) {
                initialItems.push({
                    description: "Taxes / GST",
                    hsn_sac: "9985",
                    quantity: 1,
                    unit_price: pricing.taxes,
                    tax_percent: 0,
                    tax_amount: 0,
                    total_amount: pricing.taxes
                });
            }

            if (initialItems.length > 0) {
                setItems(initialItems);
            }
        }
    }, [pricing, quotation.detailed_quotation_data]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const handleCreateInvoice = async () => {
        const invoiceData = {
            quotation_id: quotation.id,
            tcs_percent: tcsEnabled ? tcsPercent : 0,
            tax_amount: parseFloat(taxAmount),
            tcs_amount: parseFloat(tcsAmount),
            total_amount: parseFloat(totalAmount),
            items,
        };

        setCreatedInvoice(invoiceData);
        setStep(2);

        // try {
        //   setLoading(true);

        //   // const invoiceData = {
        //   //   quotation_id: quotation.id,
        //   //   tcs_percent: tcsEnabled ? tcsPercent : 0,
        //   //   subtotal: parseFloat(subtotal),
        //   //   tax_amount: parseFloat(taxAmount),
        //   //   tcs_amount: parseFloat(tcsAmount),
        //   //   total_amount: parseFloat(totalAmount),
        //   //   advance_amount: parseFloat(advanceAmount),
        //   // };

        //   const invoiceData = {
        //     quotation_id: quotation.id,
        //     tcs_percent: tcsEnabled ? tcsPercent : 0,
        //     tax_amount: parseFloat(taxAmount),
        //     tcs_amount: parseFloat(tcsAmount),
        //     total_amount: parseFloat(totalAmount),
        //     items
        //   }
        // //   const response = await api.createInvoiceFromQuotation(invoiceData);

        // //   if (response.data.success) {
        // //     setCreatedInvoice({
        // //       id: response.data.invoice_id,
        // //       invoice_number: response.data.invoice_number,
        // //     });
        // //     toast.success("Invoice created successfully!");
        // //     setStep(2); // Move to payment breakup step
        // //   }
        // // } catch (error) {
        // //   console.error("Failed to create invoice:", error);
        // //   toast.error(error.response?.data?.detail || "Failed to create invoice");
        // // } finally {
        // //   setLoading(false);
        // // }
    };

    const handleBreakupCreated = () => {
        toast.success("Payment breakup created successfully!");
        onSuccess();
    };
    if (step === 2 && createdInvoice) {
        return (
            <PaymentBreakupForm
                // invoiceId={createdInvoice.id}
                // invoiceNumber={createdInvoice.invoice_number}
                // totalAmount={totalAmount}
                onSuccess={handleBreakupCreated}
                onCancel={onCancel}
                onBack={() => setStep(1)}
                createdInvoice={createdInvoice}
            />
        );
    }

    const handleRemove = (index) => {
        const updatedItems = [...items];
        updatedItems.splice(index, 1);
        setItems(updatedItems);
    };

    const handleRowChange = (index, name, value) => {
        setItems((prev) => {
            const updated = [...prev];
            const updatedItem = {
                ...updated[index],
                [name]: value,
            };

            if (name === "quantity" || name === "unit_price" || name === "tax_percent") {
                updatedItem[name] = Number(value);
            }

            const quantity = Number(updatedItem.quantity) || 0;
            const unit_price = Number(updatedItem.unit_price) || 0;
            const tax_percent = Number(updatedItem.tax_percent) || 0;

            // Calculations
            const baseAmount = quantity * unit_price;
            const taxAmount = baseAmount * (tax_percent / 100);

            updatedItem.tax_amount = taxAmount;
            updatedItem.total_amount = baseAmount + taxAmount;

            updated[index] = updatedItem;
            return updated;
        });
    };

    const handleAddNewRow = () => {
        setItems((prev) => [
            ...prev,
            {
                description: "",
                hsn_sac: "",
                quantity: 1,
                unit_price: 0,
                tax_percent: 0,
                tax_amount: 0,
                total_amount: 0,
            },
        ]);
    };

    return (
        <div className="container mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <Button variant="ghost" onClick={onCancel} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Pending Invoices
                </Button>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Create Invoice with TCS
                </h1>
                <p className="text-gray-600">
                    Step 1: Configure invoice details and TCS calculation
                </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                {/* Invoice Form */}
                <div className="lg:col-span-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <FileText className="h-5 w-5" />
                                Invoice Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Request Info */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold mb-2">
                                    Request Information
                                </h3>
                                <div className="space-y-1 text-sm text-gray-600">
                                    <p>
                                        <span className="font-medium">
                                            Trip:
                                        </span>{" "}
                                        {
                                            quotation.detailed_quotation_data
                                                ?.tripTitle
                                        }
                                    </p>
                                    <p>
                                        <span className="font-medium">
                                            Destination:
                                        </span>{" "}
                                        {
                                            quotation.detailed_quotation_data
                                                ?.city
                                        }
                                    </p>
                                    <p>
                                        <span className="font-medium">
                                            Booking Ref:
                                        </span>{" "}
                                        {
                                            quotation.detailed_quotation_data
                                                ?.bookingRef
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Pricing Fields */}
                            {/* <div className="space-y-4">
                {/* <div>
                  <Label htmlFor="subtotal">Subtotal (before tax)</Label>
                  <Input
                    id="subtotal"
                    type="number"
                    value={subtotal}
                    onChange={(e) => setSubtotal(parseFloat(e.target.value) || 0)}
                    step="0.01"
                    className="mt-1"
                  />
                </div> */}

                            {/* <div>
                  <Label htmlFor="taxAmount">Tax Amount (GST/Service Tax)</Label>
                  <Input
                    id="taxAmount"
                    type="number"
                    value={taxAmount}
                    onChange={(e) => setTaxAmount(parseFloat(e.target.value) || 0)}
                    step="0.01"
                    className="mt-1"
                  />
                </div> */}

                            {/* <div>
                  <Label htmlFor="advanceAmount">Advance Amount</Label>
                  <Input
                    id="advanceAmount"
                    type="number"
                    value={advanceAmount}
                    onChange={(e) => setAdvanceAmount(parseFloat(e.target.value) || 0)}
                    step="0.01"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Initial deposit amount required from customer
                  </p>
                </div> */}

                            {/* TCS Toggle */}
                            <div className="border-t pt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <Label
                                            htmlFor="tcs-toggle"
                                            className="text-base font-semibold"
                                        >
                                            Apply TCS (Tax Collected at Source)
                                        </Label>
                                        <p className="text-xs text-gray-500 mt-1">
                                            TCS is calculated on subtotal + tax
                                            amount
                                        </p>
                                    </div>
                                    <Switch
                                        id="tcs-toggle"
                                        checked={tcsEnabled}
                                        onCheckedChange={setTcsEnabled}
                                    />
                                </div>

                                {tcsEnabled && (
                                    <div>
                                        <Label htmlFor="tcsPercent">
                                            TCS Percentage (%)
                                        </Label>
                                        <Input
                                            id="tcsPercent"
                                            type="number"
                                            value={tcsPercent}
                                            onChange={(e) =>
                                                setTcsPercent(
                                                    parseFloat(
                                                        e.target.value,
                                                    ) || 0,
                                                )
                                            }
                                            step="0.1"
                                            min="0"
                                            max="10"
                                            className="mt-1"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* </div>
                             */}

                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-semibold text-lg text-gray-900">
                                    Invoice Items
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleAddNewRow}
                                    className="
                                        bg-orange-600 
                                        text-white 
                                        px-4 py-2 
                                        rounded-lg 
                                        font-medium
                                        transition-all duration-200
                                        hover:bg-orange-700
                                        active:scale-95
                                        focus:outline-none 
                                        focus:ring-2 
                                        focus:ring-orange-400
                                        text-sm
                                    "
                                    data-testid="add-invoice-item-btn"
                                >
                                    + Add Item
                                </button>
                            </div>

                            <div className="overflow-x-auto border border-gray-200 rounded-xl">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-900 whitespace-nowrap">Description</th>
                                            <th scope="col" className="px-3 py-3 text-left font-semibold text-gray-900 whitespace-nowrap w-24">HSN/SAC</th>
                                            <th scope="col" className="px-3 py-3 text-left font-semibold text-gray-900 whitespace-nowrap w-20">Qty</th>
                                            <th scope="col" className="px-3 py-3 text-left font-semibold text-gray-900 whitespace-nowrap w-36">Unit Price (₹)</th>
                                            <th scope="col" className="px-3 py-3 text-left font-semibold text-gray-900 whitespace-nowrap w-24">Tax %</th>
                                            <th scope="col" className="px-3 py-3 text-right font-semibold text-gray-900 whitespace-nowrap w-36">Total (₹)</th>
                                            <th scope="col" className="px-4 py-3 text-center font-semibold text-gray-900 whitespace-nowrap w-16">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {items.map((itm, index) => (
                                            <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-4 py-2.5">
                                                    <input
                                                        type="text"
                                                        value={itm.description || ""}
                                                        onChange={(e) => handleRowChange(index, "description", e.target.value)}
                                                        className="w-full px-2.5 py-1.5 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-gray-900"
                                                        placeholder="e.g. Flight Tickets"
                                                    />
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        type="text"
                                                        value={itm.hsn_sac || ""}
                                                        onChange={(e) => handleRowChange(index, "hsn_sac", e.target.value)}
                                                        className="w-full px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-gray-900"
                                                        placeholder="9964"
                                                    />
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        type="number"
                                                        value={itm.quantity}
                                                        onChange={(e) => handleRowChange(index, "quantity", e.target.value)}
                                                        className="w-full px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-gray-900 text-center"
                                                        min="1"
                                                    />
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        type="number"
                                                        value={itm.unit_price}
                                                        onChange={(e) => handleRowChange(index, "unit_price", e.target.value)}
                                                        className="w-full px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-gray-900"
                                                        step="0.01"
                                                    />
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        type="number"
                                                        value={itm.tax_percent}
                                                        onChange={(e) => handleRowChange(index, "tax_percent", e.target.value)}
                                                        className="w-full px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-gray-900"
                                                    />
                                                </td>
                                                <td className="px-3 py-2.5 text-right font-medium text-gray-900 whitespace-nowrap">
                                                    {formatCurrency(itm.total_amount || 0)}
                                                </td>
                                                <td className="px-4 py-2.5 text-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemove(index)}
                                                        className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded-lg transition-colors inline-flex items-center justify-center"
                                                        title="Remove item"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {items.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="text-center py-6 text-gray-500 font-medium">
                                                    No items added yet. Click "+ Add Item" to start.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Calculation Summary */}
                <div className="lg:col-span-1">
                    <Card className="sticky top-6">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <Calculator className="h-5 w-5" />
                                Calculation
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {/* <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div> */}
                            <div className="flex justify-between items-start text-sm">
                                <span className="text-gray-600 whitespace-nowrap">Air Cost:</span>
                                <div className="text-right">
                                    <span className="font-medium block">
                                        {formatCurrency(pricing.airCost || 0)}
                                    </span>
                                    {inputCurrency !== "INR" && (
                                        <span className="text-gray-500 font-normal text-[11px] block">
                                            {formatOriginalCurrency(originalPricing.airCost || 0, inputCurrency)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-between items-start text-sm">
                                <span className="text-gray-600 whitespace-nowrap">Visa Cost:</span>
                                <div className="text-right">
                                    <span className="font-medium block">
                                        {formatCurrency(pricing.visaCost || 0)}
                                    </span>
                                    {inputCurrency !== "INR" && (
                                        <span className="text-gray-500 font-normal text-[11px] block">
                                            {formatOriginalCurrency(originalPricing.visaCost || 0, inputCurrency)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-between items-start text-sm">
                                <span className="text-gray-600 whitespace-nowrap">Railway Cost:</span>
                                <div className="text-right">
                                    <span className="font-medium block">
                                        {formatCurrency(pricing.railwayCost || 0)}
                                    </span>
                                    {inputCurrency !== "INR" && (
                                        <span className="text-gray-500 font-normal text-[11px] block">
                                            {formatOriginalCurrency(originalPricing.railwayCost || 0, inputCurrency)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-between items-start text-sm">
                                <span className="text-gray-600 whitespace-nowrap">Package Cost:</span>
                                <div className="text-right">
                                    <span className="font-medium block">
                                        {formatCurrency(pricing.packageCost || 0)}
                                    </span>
                                    {inputCurrency !== "INR" && (
                                        <span className="text-gray-500 font-normal text-[11px] block">
                                            {formatOriginalCurrency(originalPricing.packageCost || 0, inputCurrency)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {tcsEnabled && (
                                <>
                                    <div className="border-t pt-2 mt-2" />
                                    <div className="flex justify-between items-start text-sm">
                                        <span className="text-gray-600 whitespace-nowrap">
                                            TCS ({tcsPercent}%):
                                        </span>
                                        <div className="text-right">
                                            <span className="font-medium text-orange-600 block">
                                                {formatCurrency(tcsAmount)}
                                            </span>
                                            {inputCurrency !== "INR" && (
                                                <span className="text-gray-500 font-normal text-[11px] block">
                                                    {formatOriginalCurrency(tcsAmount / quotation.detailed_quotation_data?.inputCurrencyRate, inputCurrency)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="border-t pt-3 mt-3" />

                            <div className="flex justify-between items-start">
                                <span className="font-semibold text-base whitespace-nowrap">
                                    Total Amount:
                                </span>
                                <div className="text-right">
                                    <span className="font-bold text-lg text-orange-600 block">
                                        {formatCurrency(totalAmount)}
                                    </span>
                                    {inputCurrency !== "INR" && (
                                        <span className="text-gray-500 font-normal text-xs block">
                                            {formatOriginalCurrency(totalAmount / quotation.detailed_quotation_data?.inputCurrencyRate, inputCurrency)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="border-t pt-2 mt-2" />

                            {/* <div className="flex justify-between text-sm">
                <span className="text-gray-600">Advance Due:</span>
                <span className="font-medium">
                  {formatCurrency(advanceAmount)}
                </span>
              </div> */}

                            {/* <div className="flex justify-between text-sm">
                <span className="text-gray-600">Remaining:</span>
                <span className="font-medium">
                  {formatCurrency(totalAmount - advanceAmount)}
                </span>
              </div> */}

                            <Button
                                onClick={handleCreateInvoice}
                                disabled={loading || totalAmount <= 0}
                                className="w-full mt-6 bg-orange-600 hover:bg-orange-700"
                                data-testid="submit-invoice-btn"
                            >
                                <FileText className="mr-2 h-4 w-4" />
                                Payment Breakup
                            </Button>

                            <p className="text-xs text-gray-500 text-center mt-2">
                                Next: Configure payment breakup
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default InvoiceCreationForm;
